import { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Link,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-core';

import UserIdAutosuggest from '../common/UserIdAutosuggest';
import ConfirmationModalView from '../v2-common/ConfirmationModalView';
import { AuthorRow, useAuthorsTabViewModel } from './AuthorsTabViewModel';

export const AuthorsTabView = memo(function AuthorsTabView() {
  const vm = useAuthorsTabViewModel();

  if (!vm) return null;

  const allRows: readonly AuthorRow[] = [vm.owner, ...vm.coAuthorRows];

  return (
    <>
      <Container
        header={
          <Header
            variant="h3"
            counter={`(${allRows.length})`}
            description="The owner and co-authors who can edit this content."
          >
            Authors
          </Header>
        }
      >
        <SpaceBetween size="m">
          <SpaceBetween direction="horizontal" size="xs">
            <UserIdAutosuggest
              onUserSelected={(
                userId: string | undefined,
                userName: string | undefined,
                validationError: string | undefined,
              ) => {
                if (!validationError && userId && userName) {
                  vm.selectUser(userId, userName);
                }
              }}
            />
            <Button onClick={vm.addCoAuthor} loading={vm.isAdding} disabled={!vm.selectedUser}>
              Add co-author
            </Button>
          </SpaceBetween>
          {vm.addError && <Alert type="error">{vm.addError}</Alert>}
          <Table<AuthorRow>
            columnDefinitions={[
              {
                id: 'name',
                header: 'Name',
                cell: (row) => (
                  <Box>
                    {row.userName}
                    {row.isOwner && (
                      <Box variant="span" color="text-status-info">
                        {' '}
                        (Owner)
                      </Box>
                    )}
                  </Box>
                ),
              },
              {
                id: 'alias',
                header: 'Alias',
                width: 140,
                cell: (row) =>
                  row.alias ? (
                    <Link href={`https://phonetool.amazon.com/users/${row.alias}`} external>
                      {row.alias}
                    </Link>
                  ) : null,
              },
              {
                id: 'actions',
                header: '',
                width: 56,
                minWidth: 56,
                cell: (row) =>
                  !row.isOwner ? (
                    <Button
                      variant="icon"
                      iconName="remove"
                      ariaLabel={`Remove ${row.userName}`}
                      onClick={() => vm.removeCoAuthor(row.userId)}
                    />
                  ) : null,
              },
            ]}
            items={allRows as AuthorRow[]}
            trackBy="userId"
            variant="embedded"
          />
        </SpaceBetween>
      </Container>
      {vm.deleteConfirmation && (
        <ConfirmationModalView
          header="Remove co-author"
          body={
            <SpaceBetween size="s">
              <Box variant="p">
                Are you sure you want to remove <strong>{vm.deleteConfirmation.userName}</strong> as
                a co-author?
              </Box>
              {vm.removeError && <Alert type="error">{vm.removeError}</Alert>}
            </SpaceBetween>
          }
          confirmLabel="Remove"
          isConfirming={vm.isRemoving}
          onDismiss={vm.cancelRemove}
          onConfirm={vm.confirmRemove}
        />
      )}
    </>
  );
});
